import type {
  ImpersonationEndReason,
  ImpersonationState,
} from "../../supabase/impersonation";
import { Database } from "../../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@punchup/sentry";

/**
 * public.impersonation_sessions: one row per login-as session, keyed by the
 * target session's id. The server writes it with the service role (RLS denies
 * every client), so every function here takes an admin-scoped client.
 * The expire cron revokes each session past its limit
 * (docs/specs/authentication.md → "Login-as (Impersonation)").
 */

type Row = Database["public"]["Tables"]["impersonation_sessions"]["Row"];

const toState = (row: Row): ImpersonationState => ({
  sessionId: row.session_id,
  adminUserId: row.admin_user_id,
  adminEmail: row.admin_email,
  targetUserId: row.target_user_id,
  startedAt: row.started_at,
});

/** Records a new login-as session. Throws on failure: login-as must not run unrecorded. */
export async function insertImpersonationSession(
  supabase: SupabaseClient<Database>,
  state: ImpersonationState
): Promise<void> {
  const { error } = await supabase.from("impersonation_sessions").insert({
    session_id: state.sessionId,
    admin_user_id: state.adminUserId,
    admin_email: state.adminEmail,
    target_user_id: state.targetUserId,
    started_at: state.startedAt,
  });
  if (error) {
    logException(error, {
      data: `[impersonation-sessions] insert failed for ${state.sessionId}`,
    });
    throw error;
  }
}

/** Removes the row of a login-as that was undone before it started. */
export async function deleteImpersonationSession(
  supabase: SupabaseClient<Database>,
  sessionId: string
): Promise<void> {
  const { error } = await supabase
    .from("impersonation_sessions")
    .delete()
    .eq("session_id", sessionId);
  if (error) {
    logException(error, {
      data: `[impersonation-sessions] delete failed for ${sessionId}`,
    });
  }
}

/**
 * The open (not ended) login-as session with this id, or null. Throws on a read
 * error: callers use the row to refuse or end an impersonation, and a missing
 * answer must not read as "no impersonation".
 */
export async function findOpenImpersonationSession(
  supabase: SupabaseClient<Database>,
  sessionId: string
): Promise<ImpersonationState | null> {
  const { data, error } = await supabase
    .from("impersonation_sessions")
    .select("*")
    .eq("session_id", sessionId)
    .is("ended_at", null)
    .maybeSingle();
  if (error) {
    logException(error, {
      data: `[impersonation-sessions] read failed for ${sessionId}`,
    });
    throw error;
  }
  return data ? toState(data) : null;
}

/**
 * Marks the session ended, if nothing has yet. True when this call ended it:
 * the caller then records the end, so it is recorded once however many paths
 * (tabs, the cron, a replacing login-as) try. `"unavailable"` on a write error:
 * the caller records the end anyway.
 */
export async function claimImpersonationEnd(
  supabase: SupabaseClient<Database>,
  sessionId: string,
  reason: ImpersonationEndReason,
  endedAt: string
): Promise<boolean | "unavailable"> {
  const { data, error } = await supabase
    .from("impersonation_sessions")
    .update({ ended_at: endedAt, end_reason: reason })
    .eq("session_id", sessionId)
    .is("ended_at", null)
    .select("session_id");
  if (error) {
    logException(error, {
      data: `[impersonation-sessions] end failed for ${sessionId}`,
    });
    return "unavailable";
  }
  return (data ?? []).length > 0;
}

/** Open sessions that started at or before `startedBefore`, oldest first. Throws on a read error. */
export async function listExpiredImpersonationSessions(
  supabase: SupabaseClient<Database>,
  startedBefore: string,
  limit: number
): Promise<ImpersonationState[]> {
  const { data, error } = await supabase
    .from("impersonation_sessions")
    .select("*")
    .is("ended_at", null)
    .lte("started_at", startedBefore)
    .order("started_at", { ascending: true })
    .limit(limit);
  if (error) {
    logException(error, {
      data: "[impersonation-sessions] expired scan failed",
    });
    throw error;
  }
  return (data ?? []).map(toState);
}

/** Revokes one auth session (public.revoke_auth_session). Throws on failure. */
export async function revokeAuthSession(
  supabase: SupabaseClient<Database>,
  sessionId: string
): Promise<void> {
  const { error } = await supabase.rpc("revoke_auth_session", {
    p_session_id: sessionId,
  });
  if (error) {
    logException(error, {
      data: `[impersonation-sessions] revoke failed for ${sessionId}`,
    });
    throw error;
  }
}
